import {
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  Paper,
  Menu,
  MenuItem,
  Portal,
  Popper,
  Tooltip,
} from "@mui/material";
import { ListItemToggle } from "components/Settings/ListItemToggle";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { CollapsibleConfigStyles } from "pages/DashboardPages/ProcessTilesControls/CollapsibleConfigStyles";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { css } from "styled-components/macro";
import { forwardRef, useEffect, useRef, useState } from "react";
import { LabelWithSubtitle } from "components/Settings/SettingsPage";
import { TimeZoneController } from "components/Settings/TimeZoneController";
import { AUTO_NEXT_SHIFT_DELAY } from "constants/intervals";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { DayStartControls } from "components/Settings/DayStartControls";
import {
  BREAKPOINT_DESKTOP,
  Z_INDEX,
  getIsCypressRunning,
} from "constants/constants";
import { useDayStartChanged, useIsDarkMode } from "store/atoms";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { MoreVert, Settings as SettingsIcon } from "@mui/icons-material";
import { useCopyClipboard } from "@lokibai/react-use-copy-clipboard";
import qs, { StringifyOptions } from "query-string";
import { useSelector } from "store/useSelector";
import { useLocation } from "react-router-dom";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useToggleIsCustomRangeSelector } from "pages/DashboardPages/dashboardPagesUtils";
import { StyledLinkIcon } from "./ButtonShareRange";
import useQueryString from "utils/customHooks/useQueryString";
import { useMount } from "react-use";
import { createGlobalStyle } from "styled-components/macro";

export default function RangeSelectorConfig() {
  const parsedQuery = useQueryString();
  const { isTrendsPage } = useGetPageFromPath();
  const [isDateRangeMode, setIsDateRangeMode] = useState(isTrendsPage);
  const isStorybook = useIsStorybook();
  useMount(() => {
    if (parsedQuery.ge && parsedQuery.lt && !isStorybook) {
      setIsDateRangeMode(true);
    }
  });

  return (
    <>
      {isDateRangeMode ? <DateRangeModeGlobalCss /> : null}
      <MoreMenu {...{ isDateRangeMode, setIsDateRangeMode }} />
      <BtnShowConfig />
    </>
  );
}
const DateRangeModeGlobalCss = createGlobalStyle`
@media (max-width: ${BREAKPOINT_DESKTOP}px){
  .Card_RangeSelector{
    padding-right:48px;
  }
}
`;
function BtnShowConfig() {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { translate } = useFlightTranslation();
  const anchorRef = useRef<any>(null);
  return (
    <>
      <Tooltip
        title={translate("Configure_Range_Selector")}
        placement="bottom-start"
        PopperProps={{ className: "btnOpenConfigTooltip" }}
        disableInteractive={true}
      >
        <IconButton
          ref={anchorRef}
          className="btnRangeSelectorConfig"
          size="small"
          onClick={() => setIsConfigOpen(p => !p)}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <RangeSelectorConfigMenu
        {...{ isConfigOpen, setIsConfigOpen }}
        ref={anchorRef}
      />
    </>
  );
}

function ToggleStayOnCurrentShift() {
  const { shouldStayOnCurrentShift, setShouldStayOnCurrentShift } =
    useSettings();

  const handleChange = () => {
    setShouldStayOnCurrentShift(!shouldStayOnCurrentShift);
  };

  const { translate } = useFlightTranslation();

  return (
    <ListItemToggle
      className={`switchWrapper${shouldStayOnCurrentShift ? " checked" : ""}`}
      checked={shouldStayOnCurrentShift}
      onChange={handleChange}
      label={
        <LabelWithSubtitle
          label={translate("Stay_on_current_shift")}
          subtitle={translate("Stay_on_current_shift_explanation", "", {
            minutes: (AUTO_NEXT_SHIFT_DELAY / ONE_MINUTE_OF_MS).toFixed(0),
          })}
        />
      }
      style={{ padding: "0px 8px" }}
      labelStyle={{ padding: "0px 8px" }}
    />
  );
}
function ToggleAutoCompactLayout() {
  const {
    isHubletPageCompactLayoutDisabled,
    setIsHubletPageCompactLayoutDisabled,
  } = useSettings();

  const handleChange = () => {
    setIsHubletPageCompactLayoutDisabled(!isHubletPageCompactLayoutDisabled);
  };

  const { translate } = useFlightTranslation();

  return (
    <ListItemToggle
      className={`switchWrapper${
        !isHubletPageCompactLayoutDisabled ? " checked" : ""
      }`}
      checked={!isHubletPageCompactLayoutDisabled}
      onChange={handleChange}
      label={
        <LabelWithSubtitle
          label={translate("Auto_compact_layout")}
          subtitle={translate("Auto_compact_layout_explanation", "", {
            minutes: (AUTO_NEXT_SHIFT_DELAY / ONE_MINUTE_OF_MS).toFixed(0),
          })}
        />
      }
      style={{ padding: "8px 8px 0px" }}
      labelStyle={{ padding: "0px 8px" }}
    />
  );
}

const RangeSelectorConfigMenu = forwardRef(
  (
    {
      isConfigOpen,
      setIsConfigOpen,
    }: {
      isConfigOpen: boolean;
      setIsConfigOpen: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref: any
  ) => {
    // hack fix for z-index issues on some lower elements appearing overtop of the menu
    useEffect(() => {
      const trendChartCard = document.querySelector<HTMLElement>(
        `[data-component-name='Trend Chart']`
      );
      if (trendChartCard?.style) {
        trendChartCard.style.pointerEvents = isConfigOpen ? "none" : "";
      }
      const groupSummaryCardTopBar = document.querySelector<HTMLElement>(
        `[data-testid="card-topbar-Group-Summary"`
      );
      if (groupSummaryCardTopBar?.style) {
        groupSummaryCardTopBar.style.zIndex = isConfigOpen ? "0" : "";
      }
      const groupMetrics = document.querySelector<HTMLElement>(`.metrics`);
      if (groupMetrics?.style) {
        groupMetrics.style.zIndex = isConfigOpen ? "0" : "";
      }
    }, [isConfigOpen]);
    const [isDarkMode] = useIsDarkMode();
    return (
      <Popper
        open={isConfigOpen}
        anchorEl={ref.current}
        placement="left-start"
        style={{ zIndex: Z_INDEX.L9 }}
      >
        <RangeSelectorConfigPopperContent
          {...{ isConfigOpen, setIsConfigOpen, isDarkMode }}
        />
      </Popper>
    );
  }
);

export function RangeSelectorConfigPopperContent({
  isConfigOpen,
  setIsConfigOpen,
  isDarkMode,
}: {
  isConfigOpen: boolean;
  setIsConfigOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDarkMode: boolean;
}) {
  const { isHubletPage } = useGetPageFromPath();
  const [, setDayStartChanged] = useDayStartChanged();
  const { setDayStartMsOffset } = useSettings();

  return (
    <div className="rangeSelectorConfigMenu">
      <ClickAwayListener
        onClickAway={e => {
          if (
            (e.target as any)?.nodeName &&
            (e.target as any)?.nodeName === "BODY"
          ) {
            return;
          }
          setTimeout(() => {
            if (!isConfigOpen) {
              return;
            }
            setIsConfigOpen(false);
          });
        }}
      >
        <Paper elevation={2}>
          <CollapsibleConfigStyles
            $isDarkMode={isDarkMode}
            $customCss={css`
              position: relative;
              z-index: 99999999999999999;
              .customTimeZoneDropdown {
                position: relative;
                z-index: 99999999999999999;
                max-width: 220px;
              }
            `}
          >
            <List>
              <ToggleStayOnCurrentShift />
              {isHubletPage ? <ToggleAutoCompactLayout /> : null}
              <ListItem style={{ alignItems: "start" }}>
                <LabelWithSubtitle
                  label={"Day start offset:"}
                  subtitle={
                    <>
                      <p>• Determines 24-hour start time</p>
                      <p style={{ width: "176%" }}>
                        • Determines shifts displayed: negative shows past
                        shifts, positive shows future shifts
                      </p>
                    </>
                  }
                />
                <DayStartControls />
              </ListItem>
              <ListItem
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                }}
              >
                <TimeZoneController />
                <div
                  style={{
                    fontSize: 12,
                    lineHeight: 1.5,
                    marginTop: 4,
                  }}
                >
                  <p>• Simulate being in a different time zone</p>
                  <p>
                    • Displayed time ranges are adjusted, but data remains the
                    same
                  </p>
                </div>
              </ListItem>
            </List>
            {getIsCypressRunning() && (
              <Portal container={document.body}>
                <button
                  style={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    zIndex: 9,
                  }}
                  className="cypressTest-changeDayStartTime"
                  onClick={() => {
                    const [hoursNum, minutesNum] = [10, 0];
                    const newDayStartOffsetMs =
                      ONE_MINUTE_OF_MS * (minutesNum + hoursNum * 60);
                    setDayStartMsOffset(newDayStartOffsetMs);
                    setDayStartChanged(true);
                  }}
                >
                  cypress_only_button
                </button>
              </Portal>
            )}
          </CollapsibleConfigStyles>
        </Paper>
      </ClickAwayListener>
    </div>
  );
}

function MoreMenu({ isDateRangeMode, setIsDateRangeMode }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();

  const { pathname, search } = useLocation();
  // pathname is not guaranteed to be uri encoded (i.e. "Line 1" needs to be encoded as "Line%201" )
  const pathnameStrings = pathname.split("/");
  const uriEncodedPathname = pathnameStrings.reduce(
    (prev, curr) =>
      prev + "/" + (curr.includes(" ") ? encodeURIComponent(curr) : curr)
  );
  const { origin } = window.location;
  const focusRange = useSelector(state => state.legacy.focusRange);
  const query = qs.parse(search);
  if (focusRange) {
    query.ge = String(focusRange.ge);
    query.lt = String(focusRange.lt);
  }

  const order = ["view", "ge", "lt"];
  const stringifyOptions: StringifyOptions = {
    sort: (itemLeft, itemRight) =>
      order.indexOf(itemLeft) - order.indexOf(itemRight),
  };
  const searchWithRange = qs.stringify(query, stringifyOptions);

  const pathToShare = focusRange
    ? `${origin}${uriEncodedPathname}${
        searchWithRange ? "?" + searchWithRange : ""
      }`
    : null;

  const [, setCopied] = useCopyClipboard(pathToShare);
  const [copiedValue, setCopiedValue] = useState(null as string | null);
  const copied = Boolean(copiedValue && copiedValue === pathToShare);
  const handleCopyClipboard = () => {
    setCopied(pathToShare);
    setCopiedValue(pathToShare);
    setTimeout(() => setCopiedValue(null), 2500);
  };
  const toggleIsCustomRangeSelector = useToggleIsCustomRangeSelector();

  return (
    <>
      <IconButton size="small" className="btnMoreMenu" onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu
        disableScrollLock={true}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        PaperProps={{
          className: "moreMenuPaper",
        }}
      >
        <MenuItem
          onClick={() => {
            setIsDateRangeMode(!isDateRangeMode);
            toggleIsCustomRangeSelector();
            handleClose();
          }}
        >
          {translate(isDateRangeMode ? "Day & Shift Mode" : "Date_Range_Mode")}
        </MenuItem>
        <MenuItem onClick={handleCopyClipboard}>
          <StyledLinkIcon
            $isDarkMode={isDarkMode}
            style={{ marginRight: 4, transform: `scale(0.8)` }}
          />
          {translate(copied ? "Copied!" : "Copy_link_to_this_time_range")}
        </MenuItem>
      </Menu>
    </>
  );
}
